import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { ManagedUserRoles } from "@redotech/redo-model/roles";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { createContext, memo, ReactNode, useContext, useState } from "react";
import { PinVerificationModal } from "./pin-verification-modal";

interface ModalOptions {
  title?: string;
  description?: string;
}

interface PinVerificationContextValue {
  executeWithPinCheck: (
    action: () => void,
    config: PinVerificationConfig,
    modalOptions?: ModalOptions,
  ) => void;
}

export enum PinVerificationType {
  Scan = "scan",
  ExpensiveLabel = "expensiveLabel",
  SelectRate = "selectRate",
  VoidLabel = "voidLabel",
  Reship = "reship",
  ReprintLabel = "reprintLabel",
  ReprintPackingSlip = "reprintPackingSlip",
  ContinueToScanPageOnOutdatedPackingSlip = "continueToScanPageOnOutdatedPackingSlip",
  MergeAfterPackingSlipPrinted = "mergeAfterPackingSlipPrinted",
  OverrideNotReceived = "overrideNotReceived",
  CancelOrder = "cancelOrder",
  MarkFulfilled = "markFulfilled",
  BypassWmsSotCheck = "bypassWmsSotCheck",
  CreatePurchaseOrder = "createPurchaseOrder",
}

interface ManualScanConfig {
  type: PinVerificationType.Scan;
}
interface ManualSelectRateConfig {
  type: PinVerificationType.SelectRate;
}
interface ExpensiveLabelConfig {
  type: PinVerificationType.ExpensiveLabel;
  labelCost: number;
}

interface VoidLabelConfig {
  type: PinVerificationType.VoidLabel;
}

interface ReshipConfig {
  type: PinVerificationType.Reship;
}

interface ReprintLabelConfig {
  type: PinVerificationType.ReprintLabel;
}

interface ReprintPackingSlipConfig {
  type: PinVerificationType.ReprintPackingSlip;
}

interface ContinueToScanPageOnOutdatedPackingSlipConfig {
  type: PinVerificationType.ContinueToScanPageOnOutdatedPackingSlip;
}

interface MergeAfterPackingSlipPrintedConfig {
  type: PinVerificationType.MergeAfterPackingSlipPrinted;
}

interface OverrideNotReceivedConfig {
  type: PinVerificationType.OverrideNotReceived;
}

interface CancelOrderConfig {
  type: PinVerificationType.CancelOrder;
}

interface MarkFulfilledConfig {
  type: PinVerificationType.MarkFulfilled;
}

interface BypassWmsSotCheckConfig {
  type: PinVerificationType.BypassWmsSotCheck;
}

interface CreatePurchaseOrderConfig {
  type: PinVerificationType.CreatePurchaseOrder;
}

export type PinVerificationConfig =
  | ManualScanConfig
  | ManualSelectRateConfig
  | ExpensiveLabelConfig
  | VoidLabelConfig
  | ReshipConfig
  | ReprintLabelConfig
  | ReprintPackingSlipConfig
  | ContinueToScanPageOnOutdatedPackingSlipConfig
  | MergeAfterPackingSlipPrintedConfig
  | OverrideNotReceivedConfig
  | CancelOrderConfig
  | MarkFulfilledConfig
  | BypassWmsSotCheckConfig
  | CreatePurchaseOrderConfig;

const PinVerificationContext =
  createContext<PinVerificationContextValue | null>(null);

export const usePinVerification = () => {
  const context = useContext(PinVerificationContext);
  if (!context) {
    throw new Error(
      "usePinVerification must be used within a PinVerificationProvider",
    );
  }
  return context;
};

interface PinVerificationProviderProps {
  children: ReactNode;
}

export const PinVerificationProvider = memo(function PinVerificationProvider({
  children,
}: PinVerificationProviderProps) {
  const team = useRequiredContext(TeamContext);
  const user = useContext(UserContext);

  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const [modalOptions, setModalOptions] = useState<ModalOptions>();

  const requiresPinVerification = useHandler(
    (config: PinVerificationConfig) => {
      if (user?.managedUserRoles?.includes(ManagedUserRoles.ADMIN)) {
        return false;
      }

      // BypassWmsSotCheck works independently of the global pinChecks toggle
      if (config.type === PinVerificationType.BypassWmsSotCheck) {
        return (
          !!team?.settings?.outboundLabels?.pinChecks?.bypassWmsSotCheck
            ?.enabled && !!team?.settings?.outboundLabels?.pinChecks?.pin
        );
      }

      if (!team?.settings?.outboundLabels?.pinChecks?.enabled) {
        return false;
      }

      switch (config.type) {
        case PinVerificationType.Scan:
          return !!team?.settings?.outboundLabels?.pinChecks?.manualScan
            ?.enabled;
        case PinVerificationType.SelectRate:
          return !!team?.settings?.outboundLabels?.pinChecks?.manualSelectRate
            ?.enabled;
        case PinVerificationType.ExpensiveLabel:
          return (
            !!team?.settings?.outboundLabels?.pinChecks?.expensiveLabel
              ?.enabled &&
            config.labelCost >=
              (team?.settings?.outboundLabels?.pinChecks?.expensiveLabel
                ?.threshold || 0)
          );
        case PinVerificationType.VoidLabel:
          return !!team?.settings?.outboundLabels?.pinChecks?.voidLabel
            ?.enabled;
        case PinVerificationType.Reship:
          return !!team?.settings?.outboundLabels?.pinChecks?.reship?.enabled;
        case PinVerificationType.ReprintLabel:
          return !!team?.settings?.outboundLabels?.pinChecks?.reprintLabel
            ?.enabled;
        case PinVerificationType.ReprintPackingSlip:
          return !!team?.settings?.outboundLabels?.pinChecks?.reprintPackingSlip
            ?.enabled;
        case PinVerificationType.ContinueToScanPageOnOutdatedPackingSlip:
          return !!team?.settings?.outboundLabels?.pinChecks
            ?.continueToScanPageOnOutdatedPackingSlip?.enabled;
        case PinVerificationType.MergeAfterPackingSlipPrinted:
          return !!team?.settings?.outboundLabels?.pinChecks
            ?.mergeAfterPackingSlipPrinted?.enabled;
        case PinVerificationType.OverrideNotReceived:
          return !!team?.settings?.outboundLabels?.pinChecks
            ?.overrideNotReceived?.enabled;
        case PinVerificationType.CancelOrder:
          return !!team?.settings?.outboundLabels?.pinChecks?.cancelOrder
            ?.enabled;
        case PinVerificationType.MarkFulfilled:
          return !!team?.settings?.outboundLabels?.pinChecks?.markFulfilled
            ?.enabled;
        case PinVerificationType.CreatePurchaseOrder:
          return !!team?.settings?.outboundLabels?.pinChecks
            ?.createPurchaseOrder?.enabled;
        default:
          return false;
      }
    },
  );

  const executeWithPinCheck = useHandler(
    (
      action: () => void,
      config: PinVerificationConfig,
      modalOptions?: ModalOptions,
    ) => {
      if (requiresPinVerification(config)) {
        setPendingAction(() => action);
        setModalOptions(modalOptions);
        setPinModalOpen(true);
      } else {
        action();
      }
    },
  );

  const handlePinModalClose = useHandler(() => {
    setPinModalOpen(false);
    setPendingAction(null);
    setModalOptions(undefined);
  });

  const handlePinSuccess = useHandler(() => {
    handlePinModalClose();
    if (pendingAction) {
      pendingAction();
    }
  });

  const contextValue: PinVerificationContextValue = { executeWithPinCheck };

  return (
    <PinVerificationContext.Provider value={contextValue}>
      {children}
      {pinModalOpen && (
        <PinVerificationModal
          description={
            modalOptions?.description ||
            "Please enter your 4-digit PIN to proceed with this action."
          }
          expectedPin={team?.settings?.outboundLabels?.pinChecks?.pin || ""}
          isOpen={pinModalOpen}
          onClose={handlePinModalClose}
          onSuccess={handlePinSuccess}
          title={modalOptions?.title || "PIN Verification Required"}
        />
      )}
    </PinVerificationContext.Provider>
  );
});
